import { PLAN_PHASE, PLAN_PHASE_LABELS, TRAINING_LIMITS, formatLoadParts, loadDeviation, recommendedMinutes, type PlanPhase, type TrainingExerciseDto } from '@ukinfo/shared';
import { useState } from 'react';
import {
  draftItemFromExercise,
  draftSignature,
  draftTotalMinutes,
  durationHint,
  freeDraftItem,
  hasErrors,
  itemMinutes,
  moveItem,
  phaseMinutes,
  validateDraft,
  type DraftItem,
  type PlanDraft,
} from '../../lib/training';
import { useUnsavedChanges } from '../../lib/useUnsavedChanges';
import { Button, cn, Dialog, Field, Input, Textarea } from '../ui';
import { CategoryBadge } from './common';
import {
  EMPTY_FILTER,
  ExerciseFilterBar,
  ExerciseListState,
  ExercisePickRow,
  useExerciseList,
  useFocusOptions,
  type ExerciseFilter,
} from './ExerciseBrowser';
import { PitchDiagram } from './PitchDiagram';

/**
 * Einheit bearbeiten - als Vorlage oder fuer ein Training.
 *
 * Drei Phasen mit je einer Liste von Eintraegen. Sortiert wird mit Hoch/Runter
 * (kein Ziehen: am Handy zu fummelig). Die Uebungsauswahl ersetzt im selben
 * Dialog kurz das Formular, damit nie zwei Dialoge uebereinander liegen.
 */

function ArrowIcon({ up }: { up: boolean }): JSX.Element {
  return (
    <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={up ? 'M5 12l5-5 5 5' : 'M5 8l5 5 5-5'} />
    </svg>
  );
}

function IconButton({ label, onClick, disabled, children, danger }: { label: string; onClick: () => void; disabled?: boolean; children: JSX.Element | string; danger?: boolean }): JSX.Element {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex h-11 w-11 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 disabled:opacity-30 disabled:hover:bg-transparent sm:h-9 sm:w-9',
        danger && 'hover:bg-rose-50 hover:text-rose-700',
      )}
    >
      {children}
    </button>
  );
}

function ItemRow({
  item,
  index,
  count,
  error,
  onChange,
  onMove,
  onRemove,
}: {
  item: DraftItem;
  index: number;
  count: number;
  error: string | undefined;
  onChange: (patch: Partial<DraftItem>) => void;
  onMove: (direction: -1 | 1) => void;
  onRemove: () => void;
}): JSX.Element {
  const free = item.exerciseId === null && item.catalogKey === null;
  const name = item.title.trim() || 'freier Eintrag';
  // Empfehlung der Uebung: ein Tipp setzt die Minuten darauf; weicht der Plan deutlich ab, steht ein leiser Hinweis dabei.
  const recommended = item.load ? recommendedMinutes(item.load) : null;
  const deviation = item.load ? loadDeviation(itemMinutes(item), item.load) : null;
  return (
    <li className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="flex items-start gap-2">
        {item.diagram && (
          <div className="w-14 shrink-0 sm:w-16">
            <PitchDiagram diagram={item.diagram} className="max-h-16 rounded-md" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          {free ? (
            <Input
              aria-label="Bezeichnung"
              placeholder="Bezeichnung, z. B. Trinkpause"
              value={item.title}
              maxLength={TRAINING_LIMITS.title}
              onChange={(event) => onChange({ title: event.target.value })}
            />
          ) : (
            <>
              <p className="break-words font-medium text-slate-900">{item.title}</p>
              {item.category && <span className="mt-1 inline-block"><CategoryBadge category={item.category} /></span>}
            </>
          )}
        </div>
        <div className="flex shrink-0">
          <IconButton label={`${name} nach oben`} disabled={index === 0} onClick={() => onMove(-1)}><ArrowIcon up /></IconButton>
          <IconButton label={`${name} nach unten`} disabled={index === count - 1} onClick={() => onMove(1)}><ArrowIcon up={false} /></IconButton>
          <IconButton label={`${name} entfernen`} danger onClick={onRemove}>
            <svg viewBox="0 0 20 20" className="h-4 w-4" fill="currentColor" aria-hidden="true">
              <path d="M6.3 5A1 1 0 004.9 6.4L8.6 10l-3.7 3.6a1 1 0 101.4 1.4l3.7-3.6 3.6 3.6a1 1 0 001.4-1.4L11.4 10l3.6-3.6a1 1 0 00-1.4-1.4L10 8.6 6.3 5z" />
            </svg>
          </IconButton>
        </div>
      </div>
      <div className="mt-2 grid grid-cols-[6rem_1fr] gap-2">
        <Input
          type="number"
          inputMode="numeric"
          min={1}
          max={TRAINING_LIMITS.minutes}
          aria-label={`Minuten ${name}`}
          placeholder="Min."
          value={item.minutes}
          onChange={(event) => onChange({ minutes: event.target.value })}
        />
        <Input
          aria-label={`Notiz ${name}`}
          placeholder="Notiz (optional)"
          value={item.note}
          maxLength={TRAINING_LIMITS.itemNote}
          onChange={(event) => onChange({ note: event.target.value })}
        />
      </div>
      {item.load && recommended !== null && (
        <div className="mt-1 flex flex-wrap items-center gap-x-3 text-xs">
          <button
            type="button"
            data-testid="empfehlung"
            aria-label={`Empfehlung übernehmen für ${name}: ${formatLoadParts(item.load).join(', ')} – ${recommended} Min. setzen`}
            onClick={() => onChange({ minutes: String(recommended) })}
            className="min-h-11 text-left text-slate-600 sm:min-h-0"
          >
            Empfohlen: {formatLoadParts(item.load).join(' · ')}
            {itemMinutes(item) !== recommended && <span className="font-medium text-brand-700"> · {recommended} Min. setzen</span>}
          </button>
          {deviation && (
            <span data-testid="empfehlung-hinweis" className="text-amber-700">
              {deviation === 'below' ? 'Weniger als empfohlen' : 'Mehr als empfohlen'}
            </span>
          )}
        </div>
      )}
      {error && <p role="alert" className="error-text">{error}</p>}
    </li>
  );
}

export function PlanEditor({
  title,
  mode,
  initial,
  plannedMinutes = null,
  saving,
  serverError,
  onSave,
  onClose,
}: {
  title: string;
  /** Vorlage (Name Pflicht) oder Einheit eines Trainings (Name optional). */
  mode: 'template' | 'session';
  initial: PlanDraft;
  /** Dauer des Trainings in Minuten, wenn bekannt. */
  plannedMinutes?: number | null;
  saving: boolean;
  serverError?: string | null;
  onSave: (draft: PlanDraft) => void;
  onClose: () => void;
}): JSX.Element {
  const [startSignature] = useState(() => draftSignature(initial));
  const [draft, setDraft] = useState<PlanDraft>(initial);
  const [errors, setErrors] = useState<ReturnType<typeof validateDraft>>({ items: {} });
  const [picking, setPicking] = useState<PlanPhase | null>(null);
  const [filter, setFilter] = useState<ExerciseFilter>(EMPTY_FILTER);
  const [justAdded, setJustAdded] = useState<string | null>(null);

  const list = useExerciseList(filter, picking !== null);
  const focusOptions = useFocusOptions(picking !== null);

  const requestClose = useUnsavedChanges(true, draftSignature(draft) !== startSignature, onClose, saving);

  const setItems = (phase: PlanPhase, update: (items: DraftItem[]) => DraftItem[]): void =>
    setDraft((current) => ({ ...current, items: { ...current.items, [phase]: update(current.items[phase]) } }));

  const total = draftTotalMinutes(draft);
  const hint = durationHint(total, plannedMinutes);

  const submit = (): void => {
    const found = validateDraft(draft, mode === 'template');
    setErrors(found);
    if (!hasErrors(found)) onSave(draft);
  };

  const pick = (exercise: TrainingExerciseDto): void => {
    if (picking === null) return;
    setItems(picking, (items) => [...items, draftItemFromExercise(exercise)]);
    setJustAdded(exercise.title);
  };

  if (picking !== null) {
    return (
      <Dialog
        open
        onClose={() => setPicking(null)}
        title={`Übung hinzufügen · ${PLAN_PHASE_LABELS[picking]}`}
        wide
        footer={<Button onClick={() => setPicking(null)}>Fertig</Button>}
      >
        <div className="space-y-3">
          <ExerciseFilterBar filter={filter} onChange={setFilter} focusOptions={focusOptions} />
          {justAdded && (
            <p role="status" className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
              „{justAdded}“ ist in {PLAN_PHASE_LABELS[picking]} eingetragen. Wähle weitere oder tippe auf „Fertig“.
            </p>
          )}
          <ExerciseListState query={list} />
          {list.data && list.data.length > 0 && (
            <ul className="divide-y divide-slate-100">
              {list.data.map((exercise) => (
                <ExercisePickRow key={exercise.id} exercise={exercise} onPick={pick} />
              ))}
            </ul>
          )}
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      open
      onClose={requestClose}
      title={title}
      wide
      footer={
        <>
          <Button variant="secondary" onClick={requestClose} disabled={saving}>Abbrechen</Button>
          <Button onClick={submit} loading={saving}>Speichern</Button>
        </>
      }
    >
      <fieldset disabled={saving} className="min-w-0 space-y-4">
        {serverError && <p role="alert" className="rounded-lg bg-rose-50 p-3 text-sm text-rose-800">{serverError}</p>}
        {hasErrors(errors) && (
          <p role="alert" className="rounded-lg bg-rose-50 p-3 text-sm text-rose-800">Bitte prüfe die markierten Angaben. Deine Eingaben bleiben erhalten.</p>
        )}

        <Field
          label="Titel"
          required={mode === 'template'}
          error={errors.title}
          hint={mode === 'session' ? 'Ohne Titel gilt der Titel des Trainings.' : undefined}
        >
          {(id) => <Input id={id} value={draft.title} maxLength={TRAINING_LIMITS.title} onChange={(event) => setDraft({ ...draft, title: event.target.value })} />}
        </Field>
        <Field label="Schwerpunkt" hint="z. B. Passspiel unter Druck">
          {(id) => <Input id={id} value={draft.focus} maxLength={TRAINING_LIMITS.planFocus} onChange={(event) => setDraft({ ...draft, focus: event.target.value })} />}
        </Field>
        <Field label="Notizen">
          {(id) => <Textarea id={id} rows={2} value={draft.notes} maxLength={TRAINING_LIMITS.planNotes} onChange={(event) => setDraft({ ...draft, notes: event.target.value })} />}
        </Field>

        <div className="rounded-xl bg-slate-50 px-3 py-2 text-sm" aria-live="polite">
          <p className="font-medium text-slate-900">Gesamt: {total} Min.</p>
          {hint && <p className={hint.tone === 'ok' ? 'text-emerald-700' : 'text-amber-700'}>{hint.text}</p>}
        </div>
        {errors.general && <p role="alert" className="error-text">{errors.general}</p>}

        {PLAN_PHASE.map((phase) => {
          const items = draft.items[phase];
          return (
            <section key={phase} aria-label={PLAN_PHASE_LABELS[phase]} className="space-y-2">
              <div className="flex items-baseline justify-between gap-2">
                <h4 className="text-base font-semibold text-slate-900">{PLAN_PHASE_LABELS[phase]}</h4>
                <span className="text-sm text-slate-500" data-testid={`minuten-${phase}`}>{phaseMinutes(items)} Min.</span>
              </div>
              {items.length === 0 ? (
                <p className="rounded-xl border border-dashed border-slate-300 px-3 py-4 text-center text-sm text-slate-500">Noch keine Einträge</p>
              ) : (
                <ul className="space-y-2">
                  {items.map((item, index) => (
                    <ItemRow
                      key={item.key}
                      item={item}
                      index={index}
                      count={items.length}
                      error={errors.items[item.key]}
                      onChange={(patch) => setItems(phase, (prev) => prev.map((entry) => (entry.key === item.key ? { ...entry, ...patch } : entry)))}
                      onMove={(direction) => setItems(phase, (prev) => moveItem(prev, index, direction))}
                      onRemove={() => setItems(phase, (prev) => prev.filter((entry) => entry.key !== item.key))}
                    />
                  ))}
                </ul>
              )}
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="secondary" size="sm" onClick={() => { setJustAdded(null); setPicking(phase); }}>
                  Übung hinzufügen<span className="sr-only"> zu {PLAN_PHASE_LABELS[phase]}</span>
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={() => setItems(phase, (current) => [...current, freeDraftItem()])}>
                  Freier Eintrag<span className="sr-only"> in {PLAN_PHASE_LABELS[phase]}</span>
                </Button>
              </div>
            </section>
          );
        })}
      </fieldset>
    </Dialog>
  );
}
